import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { CartItem, EventCartItem, GuestModel } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { UserState } from '../../../models/state.model';
import { CustomEventService } from '../../../services/custom-event.service';
import { FormsService } from '../../../services/forms.service';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';

@Component({
  selector: 'app-coupon-form',
  templateUrl: './coupon-form.component.html',
  styleUrls: ['./coupon-form.component.scss'],
  imports: [FormsModule, TranslateModule, TranslationPipe, CalioMeetingTemplatePipe]
})
export class CouponFormComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  private formsService = inject(FormsService);
  private customEventService = inject(CustomEventService);

  @Input() protected partner: Partner;
  @Input() protected userState: UserState;
  @Input() protected cart: CartItem[];
  @Input() protected eventCart: EventCartItem[];
  @Input() protected widgetType: string;
  @Input() protected lang: string;
  @Input() protected widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() protected preApplyCoupon: string;

  private _additionalGuests: GuestModel[] = [];
  private _lastGuestCount = 0;
  @Input() set additionalGuests(value: GuestModel[]) {
    this._additionalGuests = value || [];
    const newCount = this._additionalGuests.length;
    if (this.coupon && this._lastGuestCount !== newCount) {
      this.onApplyCoupon(true);
    }
    this._lastGuestCount = newCount;
  }
  get additionalGuests(): GuestModel[] {
    return this._additionalGuests;
  }

  private _anonymousGuestCount = 0;
  private _lastAnonymousCount = 0;
  @Input() set anonymousGuestCount(value: number) {
    this._anonymousGuestCount = Number(value) || 0;
    if (this.coupon && this._lastAnonymousCount !== this._anonymousGuestCount) {
      this.onApplyCoupon(true);
    }
    this._lastAnonymousCount = this._anonymousGuestCount;
  }
  get anonymousGuestCount(): number {
    return this._anonymousGuestCount;
  }

  protected readonly widgetConstants = WIDGET_CONSTANTS;
  readonly templateContent = TRANSLATION_TEMPLATES;
  readonly templateConstant = TEMPLATE_CONSTANT;

  protected coupon: string;
  protected showError: boolean;
  protected showSuccessMsg: boolean;
  protected showEmailRequiredError: boolean;
  protected showInsufficientBalanceError: boolean;
  protected isVerifying = false;
  protected remainingBalance = 0;
  protected showRemainingBalanceNote = false;
  private couponVerifySubscription: Subscription;

  @Output() private couponSuccessEvent: EventEmitter<CouponDbModel> = new EventEmitter();
  @Output() private couponFailedEvent: EventEmitter<{
    disableBookingButton: boolean,
    invalidCoupon: boolean,
  }> = new EventEmitter();

  ngOnInit(): void {
    this.handlePreApplyCoupon();
    this.customEventService.emailChangedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handlePreApplyCoupon());
  }

  private handlePreApplyCoupon(): void {
    if (this.preApplyCoupon) {
      this.coupon = this.preApplyCoupon;
      this.onApplyCoupon();
    }
  }

  protected onApplyCoupon(isReVerify = false): void {
    if (!isReVerify) {
      this.resetErrorsState();
    }
    this.isVerifying = true;
    if (!this.coupon) {
      this.isVerifying = false;
      this.couponFailedEvent.emit({ disableBookingButton: false, invalidCoupon: true });
      this.customEventService.resetPayPalOrderDetailEvent.emit();
      return;
    }
    const isEventCart = this.widgetType === WIDGET_CONSTANTS.EVENT;
    const selectedCart = isEventCart ? this.eventCart : this.cart;
    let amount = 0;
    if (isEventCart) {
      amount = (selectedCart as EventCartItem[])?.reduce((acc, item) => acc + item.currentEvent.price, 0) || 0;
      // Total participants = booker (1) + named guests + anonymous-count guests.
      // Both guest types can coexist; multiply per-event price by the sum.
      const totalParticipants = 1 + (this.additionalGuests?.length ?? 0) + (this.anonymousGuestCount ?? 0);
      if (totalParticipants > 1) {
        amount = amount * totalParticipants;
      }
    } else {
      amount = (selectedCart as CartItem[])?.reduce((acc, item) => acc + item.total.price, 0) || 0;
    }
    const couponPayload = {
      coupon: this.coupon,
      ...(this.userState.eMail && { email: this.userState.eMail }),
      ...(amount > 0 && { amount })
    };
    this.couponVerifySubscription?.unsubscribe();
    this.couponVerifySubscription = this.formsService.applyCoupon(couponPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: CouponDbModel) => {
          this.isVerifying = false;
          this.resetErrorsState();
          this.showSuccessMsg = true;
          if (result.coupon_type_id === 7) {
            this.showRemainingBalanceNote = true;
            this.remainingBalance = +result.balance;
          }
          this.couponSuccessEvent.emit(result);
        },
        error: (error: HttpErrorResponse) => {
          this.isVerifying = false;
          this.resetErrorsState();
          const errorCode = error?.error?.errors?.[0]?.code;
          if (error?.error?.errors && error.error.errors.length === 1) {
            if (errorCode === HttpStatusCode.NotFound) {
              this.showError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (errorCode === HttpStatusCode.BadRequest) {
              this.showError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (errorCode === HttpStatusCode.Unauthorized) {
              this.showError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (errorCode === HttpStatusCode.Forbidden) {
              this.showError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (errorCode === HttpStatusCode.NotAcceptable) {
              this.showEmailRequiredError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (errorCode === HttpStatusCode.Conflict) {
              this.showInsufficientBalanceError = true;
              this.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              this.customEventService.resetPayPalOrderDetailEvent.emit();
            }
          }
        }
      });
  }

  private resetErrorsState(): void {
    this.showError = false;
    this.showSuccessMsg = false;
    this.showEmailRequiredError = false;
    this.showInsufficientBalanceError = false;
    this.showRemainingBalanceNote = false;
    this.remainingBalance = 0;
  }
}
